import { CommonModule } from '@angular/common';
import { Component, inject, input, model, SimpleChanges } from '@angular/core';
import { MatMenuModule } from '@angular/material/menu';
import { map, Observable, takeUntil, tap } from 'rxjs';
import {
  GetEnumValuesDto,
  SmartSmsRequestPeriodicPatterns,
} from 'src/app/core/api/models';
import { CalendersService, EnumsService } from 'src/app/core/api/services';
import { NumberSeparatorPipe } from 'src/app/core/pipes/number.separator.pipe';
import { DialogService } from 'src/app/core/services/dialog.service';
import { AppDomService } from 'src/app/core/services/dom.service';
import { SavedEnumsService } from 'src/app/core/services/saved-enums.service';
import { CalenderData } from 'src/app/pages/facility/calendar-event/calender/calender-data.model';
import { BaseComponentComponent } from 'src/app/shared/components/base/base-component/base-component.component';
import { TranslationModule } from '../../../../i18n/translation.module';
import {
  SmartSmsPeriodicPatternsStateService,
  SmartSmsRequestPeriodicPatternsDialogData,
} from './smart-sms-request-periodic-patterns-dialog-form/smart-sms-periodic-patterns.service';
import { SmartSmsRequestPeriodicPatternsDialogFormComponent } from './smart-sms-request-periodic-patterns-dialog-form/smart-sms-request-periodic-patterns-dialog-form.component';

@Component({
    selector: 'smart-sms-request-periodic-patterns',
    imports: [
        CommonModule,
        TranslationModule,
        NumberSeparatorPipe,
        MatMenuModule,
    ],
    templateUrl: './smart-sms-request-periodic-patterns.component.html',
    styles: ``
})
export class SmartSmsRequestPeriodicPatternsComponent extends BaseComponentComponent {
  periodicPatternService = inject(SmartSmsPeriodicPatternsStateService);
  currentService = inject(CalendersService);
  enumService = inject(EnumsService);
  dialogService = inject(DialogService);

  startDateTime = input<string>();
  endDateTime = input<string>();
  isReadonly = input<boolean>();

  selectedDays = model<string[]>();
  periodicPattern = model<SmartSmsRequestPeriodicPatterns>();
  disabled = input<boolean>();
  calenderItems: CalenderData[] = [];
  periodicPatternsEnumValues: GetEnumValuesDto[] = [];
  daysOfWeekEnumValues: GetEnumValuesDto[] = [];
  selectedSolarDays: string[] = [];

  ngOnInit(): void {
    this.getEnumValuesApi('SmartSmsRequestPeriodicPatterns')
      .pipe(this.basePipeOperators())
      .subscribe((values) => (this.periodicPatternsEnumValues = values));
    this.getEnumValuesApi('DaysOfWeek')
      .pipe(this.basePipeOperators())
      .subscribe((values) => (this.daysOfWeekEnumValues = values));
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      (changes.startDateTime || changes.endDateTime) &&
      this.startDateTime() != undefined &&
      this.endDateTime() != undefined
    )
      this.getCalendarApi(false);
  }

  onShowCalender() {
    const data: SmartSmsRequestPeriodicPatternsDialogData = {
      calenderItems: this.calenderItems,
      periodicPattern: this.periodicPattern(),
      selectedDays: this.selectedDays(),
      periodicPatternsEnumValues: this.periodicPatternsEnumValues,
      daysOfWeekEnumValues: this.daysOfWeekEnumValues,
      isReadonly: this.isReadonly(),
    };
    const modalResult = this.dialogService.openModal({
      component: SmartSmsRequestPeriodicPatternsDialogFormComponent,
      data,
    });
    modalResult
      .afterClosed()
      .pipe(
        this.basePipeOperators(),
        tap(() => {
          AppDomService.removeModalScrollClassFromHtml();
        })
      )
      .subscribe((res: SmartSmsRequestPeriodicPatternsDialogData) => {
        this.periodicPattern.set(res.periodicPattern);
        this.calenderItems = res.calenderItems;
        this.getSelectedDays();
      });
  }

  getCalendarApi(showCalender: boolean = true) {
    if (!this.startDateTime() || !this.endDateTime()) {
      this.selectedDays.set([]);
      this.selectedSolarDays = [];
      this.periodicPattern.set(null);
      return;
    }
    if (this.loading) return;
    this.loading = true;
    this.currentService
      .calendersGetCalenderByDateTime({
        StartDateTime: this.startDateTime(),
        EndDateTime: this.endDateTime(),
      })
      .pipe(this.basePipeOperators())
      .subscribe((res) => {
        if (res.isSucceed) {
          // مقداردهی و افزودن پرارپرتی ها
          this.calenderItems =
            this.periodicPatternService.mapAndInitialCalenderData(
              res.data,
              this.daysOfWeekEnumValues
            );
          // غیرفعال کردن روزهایی که در بازه زمانی انتخاب شده نیستند
          this.calenderItems = this.periodicPatternService.disableInvalidDays(
            this.calenderItems,
            this.startDateTime(),
            this.endDateTime()
          );
          this.applySelectedDays();
          if (showCalender) this.onShowCalender();
        }
      });
  }

  /** اعمال روزهایی که قبلا انتخاب شده اند */
  applySelectedDays() {
    this.calenderItems = this.periodicPatternService.applySelectedDays(
      this.calenderItems,
      this.selectedDays()
    );
    this.getSelectedDays();
  }

  getSelectedDays() {
    this.calenderItems = this.periodicPatternService.applyPeriodicPattern(
      this.calenderItems,
      this.periodicPattern()
    );
    let selectedDays = this.periodicPatternService.getSelectedDays(
      this.calenderItems
    );
    // اگر روزی انتخاب نکرده بود پیش فرض همه روزهای هفته را درنظر بگیر
    if (!selectedDays[0]?.length) {
      this.#selectAllDaysOfWeek();
      selectedDays = this.periodicPatternService.getSelectedDays(
        this.calenderItems
      );
    }
    this.selectedDays.set([]);
    this.selectedDays.set(selectedDays[0]);
    this.selectedSolarDays = selectedDays[1];
  }

  #selectAllDaysOfWeek() {
    this.periodicPattern.set(SmartSmsRequestPeriodicPatterns.DaysOfWeek);
    this.calenderItems = this.periodicPatternService.applyPeriodicPattern(
      this.calenderItems,
      this.periodicPattern()
    );
  }

  getEnumValuesApi(enumName): Observable<GetEnumValuesDto[]> {
    if (SavedEnumsService.getEnum(enumName) != null)
      return SavedEnumsService.getEnumAsObservable(enumName);
    this.enumService
      .enumsGetEnumValues({ EnumTypeName: enumName })
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((newEnum) => {
        SavedEnumsService.pushEnum({
          enumName: enumName,
          enumValue: newEnum?.data,
        });
      });
    // save enum
    return this.enumService.enumsGetEnumValues({ EnumTypeName: enumName }).pipe(
      takeUntil(this.onDestroy$),
      map((res) => res.data)
    );
  }
}
